import React from 'react';

import { graphql, Link } from 'gatsby';
import { Link as GatsbyLink } from 'gatsby-link';

import { Grid2 as Grid, styled } from '@mui/material';

import { LeadIntent, NavigationLinks } from '@mewa/types';
import {
  BreadCrumbData,
  BreadCrumbs,
  ConversionTeaserImage,
  extractTextMarking,
  ResponsiveGrid,
  SeoFooter,
  setARIATranslations,
  StageWithCta,
} from '@mewa/ui';

import { localeBaseUrl } from '../../../libs/alternative-links';
import { ContactTeaserWrapper } from '../../components/contact-teaser/contact-teaser-wrapper';
import { ContactTeaserLabels } from '../../components/header/types';
import { ServiceSegmentGridItem } from '../../components/service-segment-grid-item';
import { StrapiMewaEssentials } from '../../components/strapi/mewa-essentials/mewa-essentials';
import { GlobalPageContext } from '../../types';
import { storeLeadIntent } from '../conversion/persistence';
import { StrapiImage } from '../dynamic-content-page/content-components/image';
import { CategoryPageContentType } from './types';

export const query = graphql`
  fragment CategoryPageContentFragment on STRAPI__COMPONENT_COMPONENTS_CATEGORY_PAGE {
    categoryLinksHeadline: CategoryLinksHeadline
    conversionHeadline: ConversionHeadline
    conversionSubline: ConversionSubline
    stageHeadline: StageHeadline
    stageSubline: StageSubline
    stageImage: StageImage {
      ...StrapiImageFragment
    }
    mewaEssentials {
      ...MewaEssentialsFragment
    }
    conversionImage: ConversionImage {
      ...StrapiImageFragment
    }
    conversionCta: ConversionCta {
      url
      text
    }
    stageCta: StageCta {
      url
      text
    }
    seo: SEO {
      data {
        childMarkdownRemark {
          html
        }
      }
    }
    textBlocks {
      headline
      leftTextBlock
      rightTextBlock
    }
  }
`;

export const CategoryPage = ({
  items,
  pageContext: { lang, langPrefix, market, locale },
  categoryPageContent,
  translations,
  contactTeaserLabels,
  navigationLinks,
  seoFooter,
  breadCrumbData,
  mobileBreadCrumbData,
}: {
  items: {
    name: string;
    slug: string;
    claim: string;
    image: React.ReactElement;
  }[];
  pageContext: GlobalPageContext;
  categoryPageContent: CategoryPageContentType;
  translations: {
    general_more_information: string;
    general_form_button_label_next: string;
    general_form_button_label_back: string;
    general_consultation_cta: string;
  };
  contactTeaserLabels: ContactTeaserLabels;
  navigationLinks: NavigationLinks;
  seoFooter: {
    html: string;
    showMoreLabel: string;
    showLessLabel: string;
  };
  breadCrumbData: BreadCrumbData[];
  mobileBreadCrumbData: BreadCrumbData;
}) => {
  const {
    categoryLinksHeadline,
    conversionHeadline,
    conversionImage,
    conversionSubline,
    conversionCta,
    stageHeadline,
    stageCta,
    stageImage,
    stageSubline,
    mewaEssentials,
    textBlocks,
  }: CategoryPageContentType = categoryPageContent;

  const markedHeadline = extractTextMarking(conversionHeadline);
  const markedStageSubline = extractTextMarking(stageSubline);

  setARIATranslations({
    label_back: translations.general_form_button_label_back,
    label_next: translations.general_form_button_label_next,
  });

  return (
    <>
      <BreadCrumbs
        data={breadCrumbData}
        mobileData={mobileBreadCrumbData}
        localeBaseUrl={localeBaseUrl(locale)}
        LinkComponent={GatsbyLink}
      />
      <StageWithCta
        title={stageHeadline}
        subtitle={
          markedStageSubline.beforeMarking === '' &&
          markedStageSubline.afterMarking === '' ? (
            markedStageSubline.marking
          ) : (
            <>
              {markedStageSubline.beforeMarking}
              <strong>{markedStageSubline.marking}</strong>
              {markedStageSubline.afterMarking}
            </>
          )
        }
        image={<StrapiImage responsiveImage={stageImage} />}
        link={{
          component: Link,
          props: {
            to: stageCta.url,
          },
        }}
        labelCTA={stageCta.text}
        piwikEventNameCTA={'cta_contact_category_page_hero_stage'}
      />
      {textBlocks ? (
        <ResponsiveGrid sx={{ marginTop: 'var(--spacer-xxxl)' }}>
          <Grid size={12}>
            <Headline>{textBlocks.headline}</Headline>
          </Grid>
          <Grid size={{ xs: 12, lg: 5 }} offset={{ xs: 0, lg: 1 }}>
            <Textblock>{textBlocks.leftTextBlock}</Textblock>
          </Grid>
          <Grid size={{ xs: 12, lg: 5 }}>
            <Textblock>{textBlocks.rightTextBlock}</Textblock>
          </Grid>
        </ResponsiveGrid>
      ) : null}
      <StrapiMewaEssentials
        lang={lang}
        pressOverviewSlug={`${langPrefix}/${navigationLinks.pressOverview.slug}`}
        mewaEssentials={mewaEssentials}
      ></StrapiMewaEssentials>
      {conversionHeadline && conversionImage && (
        <ConversionTeaserImage
          headline={
            <>
              {markedHeadline.beforeMarking}
              <strong>{markedHeadline.marking}</strong>
              {markedHeadline.afterMarking}
            </>
          }
          content={conversionSubline}
          labelCTA={conversionCta.text}
          piwikEventNameCTA={'cta_contact_category_page_teaser'}
          link={{
            component: Link,
            props: {
              to: conversionCta.url,
            },
          }}
          onClick={() => {
            storeLeadIntent(LeadIntent.consultation);
          }}
          image={<StrapiImage responsiveImage={conversionImage} />}
        />
      )}
      <ResponsiveGrid>
        <Grid size={12}>
          <Headline>{categoryLinksHeadline}</Headline>
          <ServiceSegmentsGrid>
            {items.map((item, index) => (
              <ServiceSegmentGridItem
                key={index}
                langPrefix={langPrefix}
                item={item}
                general_more_information_label={
                  translations.general_more_information
                }
              />
            ))}
          </ServiceSegmentsGrid>
        </Grid>
      </ResponsiveGrid>

      <ContactTeaserWrapper
        langPrefix={langPrefix}
        navigationLinks={navigationLinks}
        contactTeaserLabels={contactTeaserLabels}
      />

      {seoFooter ? (
        <SeoFooter
          html={seoFooter.html}
          showMoreLabel={seoFooter.showMoreLabel}
          showLessLabel={seoFooter.showLessLabel}
        />
      ) : null}
    </>
  );
};

const Headline = styled('h2')(({ theme }) => ({
  ...theme.typography.h2,
  margin: '0 0 var(--spacer-l)',
  textAlign: 'center',
}));

const Textblock = styled('p')(({ theme }) => ({
  ...theme.typography.body,
  margin: 0,
}));

const ServiceSegmentsGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '30px var(--gutter)',

  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '0 9px',
  },

  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '30px 10px',
  },

  [theme.breakpoints.up('lg')]: {
    gridColumnGap: '20px',
  },
}));
